import { usersService } from "../services";
import { User } from "../services/domain/User";
import {
  all,
  call,
  put,
  takeLatest,
  select,
  takeEvery,
  getContext,
} from "redux-saga/effects";
import {
  ValidatedResponse,
  PagedResponse,
  JSONPatchDocument,
  QueryParams,
} from "@ofqual/portal-core/core/types";
import {
  loadedUsers,
  LOADING_USERS,
  ActionPayload,
  loadedUser,
  LOADING_USER,
  UPDATING_USER,
  UPDATING_USER_VALIDATION_FAIL,
  CREATING_USER,
  CREATING_USER_VALIDATION_FAIL,
  ACTIVATING_USER,
  CREATED_USER,
  savedUser,
  RESETTING_PASSWORD,
} from "./actions";
import { authSelector } from "./selectors";
import { History } from "history";

function* navigate(url: string) {
  const history: History = yield getContext("history");
  yield call(() => history.push(url));
}

function* watchUsersSaga() {
  yield takeLatest(LOADING_USERS, usersSaga);
}

function* watchUserSaga() {
  yield takeLatest(LOADING_USER, userSaga);
}

function* watchUpdateUserSaga() {
  yield takeEvery(UPDATING_USER, updateUserSaga);
}

function* watchCreateUserSaga() {
  yield takeEvery(CREATING_USER, createUserSaga);
}

function* watchActivatingUserSaga() {
  yield takeEvery(ACTIVATING_USER, activateUserSaga);
}

function* watchResettingPasswordSaga() {
  yield takeEvery(RESETTING_PASSWORD, resettingPasswordSaga);
}

function* usersSaga({ payload }: ActionPayload<QueryParams>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);
  const payloadForApi = { ...payload };
  switch (payloadForApi.active) {
    case "all":
      payloadForApi.active = undefined;
      break;
    case "false":
      payloadForApi.active = "false";
      break;
    default:
      payloadForApi.active = "true";
  }
  const users: PagedResponse<User> = yield call(
    [usersService, usersService.search],
    payloadForApi,
    { token: auth.token }
  );

  yield put(loadedUsers(users));
}

function* userSaga({ payload }: ActionPayload<string>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const user: User = yield call([usersService, usersService.get], payload, {
    token: auth.token,
  });
  yield put(loadedUser(user));
}

function* updateUserSaga({ payload }: ActionPayload<any>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);
  const { upn, patchDocument } = payload as {
    upn: string;
    patchDocument: JSONPatchDocument;
  };

  const validatedResponse: ValidatedResponse<User> = yield call(
    [usersService, usersService.updateUser],
    upn,
    patchDocument,
    {
      token: auth.token,
    }
  );

  if (validatedResponse.isValid) {
    yield put(savedUser(validatedResponse.data));
  } else {
    yield put({
      type: UPDATING_USER_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* createUserSaga({ payload }: ActionPayload<User>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const validatedResponse: ValidatedResponse<User> = yield call(
    [usersService, usersService.createUser],
    payload,
    {
      token: auth.token,
    }
  );

  if (validatedResponse.isValid) {
    yield put({ type: CREATED_USER });
    yield call(() => navigate(`/users/${validatedResponse.data.upn}`));
  } else {
    yield put({
      type: CREATING_USER_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* activateUserSaga({ payload }: ActionPayload<any>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);
  const { upn, active } = payload as {
    upn: string;
    active: boolean;
  };

  const user: User = yield call(
    [usersService, usersService.activateUser],
    upn,
    active,
    {
      token: auth.token,
    }
  );

  yield put(savedUser(user));
}

function* resettingPasswordSaga({ payload }: ActionPayload<any>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);
  const { upn } = payload as {
    upn: string;
  };

  const user: User = yield call(
    [usersService, usersService.resetPassword],
    upn,
    {
      token: auth.token,
    }
  );

  yield put(savedUser(user));
}

export function* rootSaga() {
  yield all([
    watchUsersSaga(),
    watchUserSaga(),
    watchUpdateUserSaga(),
    watchCreateUserSaga(),
    watchActivatingUserSaga(),
    watchResettingPasswordSaga(),
    // further sagas...
  ]);
}
